<template>
  <div class="workflow-detail-page" style="margin-top: 8px">
    <CollapsibleSection v-if="workflow" title="Workflow Details" :collapsible="false">
      <WorkflowDetails
        :workflow="workflow"
        @updateWorkflow="updateWorkflowDetails"
      />
    </CollapsibleSection>

    <CollapsibleSection v-if="workflow && files" title="Files">
      <ExperimentFiles
        ref="filesComp"
        :files="files"
        :folders="folders"
        :base-path="filesBasePath"
        :loadingTree="isFilesTreeLoading"
        :experimentId="workflow.id"
        :assId="workflow.id"
        :assType="'workflow'"
        :isUploading="isUploading"
        :uploadPercent="uploadPercent"
        :uploadLabel="uploadLabel"
        @addFiles="addFiles"
        @removeFile="removeFile"
      />
    </CollapsibleSection>

    <CollapsibleSection v-if="workflow" title="Activities">
      <ActionList
        :actions="actions"
        :workflowId="workflow.id"
        :workflowName="workflow.name"
        :projectID="workflow.project_id"
        :projectName="projectName"
        @addAction="addAction"
        @deleteAction="deleteAction"
        @updateActionsOrder="handleActionsReorder"
        @refreshActions="fetchActions"
      />
    </CollapsibleSection>

    <CollapsibleSection v-if="workflow" title="Materials">
      <ProjectMaterials
          :materials="materials"
          :id="workflow.id"
          :assType="'workflow'"
          :projId="workflow.project_id"
          :refreshMaterials="fetchWorkflowMaterials"
      />
    </CollapsibleSection>

    <FileUploadSession
      :visible="uploadSession.visible"
      :fileType="uploadSession.fileType"
      :entityId="uploadSession.entityId"
      :files="uploadSession.files"
      :entraId="loggedInUser?.entra_id"
      :folderId="uploadSession.folderId"
      @complete="handleUploadComplete"
      @close="handleUploadClose"
      @error="handleUploadError"
    />

    <CollapsibleSection title="Comments">
      <ExperimentComments 
        v-if="comments" 
        :comments="comments" 
        :users="users" 
        :files="files" 
        :entityId="workflow.id"
        @addComment="addComment" 
        @removeComment="removeComment" 
        @updateComment="handleUpdateComment"
      />
    </CollapsibleSection>
  </div>
</template>

<script>
import WorkflowDetails from '@/components/WorkflowDetails.vue';
import ActionList from '@/components/ActionList.vue';
import ExperimentFiles from '@/components/ExperimentFiles.vue';
import ExperimentComments from '@/components/ExperimentComments.vue';
import ProjectMaterials from "@/components/ProjectMaterials.vue";
import FileUploadSession from '@/components/FileUploadSession.vue';
import CollapsibleSection from '@/components/CollapsibleSection.vue';
import { useEntityDiscussion } from '@/composables/useEntityDiscussion';
import axios from 'axios';
import { parseFilesTree } from '@/utils/filesTree';
import { API_ENDPOINTS } from '@/services/config';
import apiService from '@/services/apiService';
import { useToast } from 'vue-toastification';
import { mapState } from 'vuex';

export default {
  components: {
    WorkflowDetails,
    ActionList,
    ExperimentFiles,
    ExperimentComments,
    ProjectMaterials,
    FileUploadSession,
    CollapsibleSection,
  },
  data() {
    return {
      workflow: null,
      actions: [],
      files: [],
      folders: [],
      isFilesTreeLoading: false,
      filesBasePath: '',
      comments: [],
      users: [],
      projectName: "",
      materials: [],
      isUploading: false,
      uploadPercent: 0,
      uploadLabel: "",
      uploadSession: {
        visible: false,
        fileType: '',
        entityId: null,
        files: [],
        folderId: null,
      },
    };
  },
  setup() {
    const toast = useToast();
    const discussion = useEntityDiscussion();
    return { toast, discussion };
  },
  created() {
    this.fetchWorkflowData();
    this.fetchActions();
    this.fetchComments();
    this.fetchUsers();
    this.fetchWorkflowMaterials()
  },
  computed: {
    ...mapState(['loggedInUser']), 
  },
  methods: {
    async fetchWorkflowData() {
      const workflowId = this.$route.params.id;
      try {
        const response = await axios.get(API_ENDPOINTS.workflow(workflowId), {
          params: {
            entra_id: this.loggedInUser.entra_id,
          },
        });
        this.workflow = response.data;
        this.fetchProjectName(this.workflow.project_id);
        console.log(response.data);
        this.fetchFiles(); // Fetch files after setting workflow
      } catch (error) {
        this.toast.error('Failed to fetch workflow data');
        console.error('Failed to fetch workflow data:', error);
      }
    },
    async fetchProjectName(projectId) {
      try {
        const response = await axios.get(API_ENDPOINTS.project(projectId), {
          params: {
            entra_id: this.loggedInUser.entra_id,
          },
        });
        this.projectName = response.data.name;
      } catch (error) {
        console.error('Error fetching project name:', error);
      }
    },
    async fetchActions() {
      try {
        const workflowId = this.$route.params.id;
        const response = await axios.get(`${API_ENDPOINTS.workflow(workflowId)}/actions`, {
          params: {
            entra_id: this.loggedInUser.entra_id,
          },
        });
        this.actions = response.data;
      } catch (error) {
        this.toast.error('Failed to fetch actions');
        console.error('Failed to fetch actions:', error);
      }
    },
    async fetchFiles() {
      const workflowId = this.workflow.id;
      this.isFilesTreeLoading = true;
      try {
        const response = await axios.get(API_ENDPOINTS.filesTree, {
          params: {
            file_type: 'workflow',
            entity_id: workflowId,
            entra_id: this.loggedInUser.entra_id,
          },
        });
        const { files, folders, basePath } = parseFilesTree(response.data);
        this.files = files;
        this.folders = folders;
        this.filesBasePath = basePath;
      } catch (error) {
        console.error('Error fetching files/folders:', error);
        await this.fetchFilesLegacy();
      } finally {
        this.isFilesTreeLoading = false;
      }
    },
    async fetchFilesLegacy() {
      const workflowId = this.workflow.id;
      try {
        const [filesRes, foldersRes] = await Promise.all([
          axios.get(API_ENDPOINTS.files, { params: { file_type: 'workflow', entity_id: workflowId, entra_id: this.loggedInUser.entra_id } }),
          axios.get(API_ENDPOINTS.folders, { params: { associative_type: 'workflow', associative_id: workflowId, entra_id: this.loggedInUser.entra_id } }),
        ]);
        this.files = filesRes.data || [];
        this.folders = (foldersRes.data || []).map((folder) => ({
          ...folder,
          id: folder?.id ?? `virtual:${folder?.internal_path || folder?.path || Math.random().toString(36).slice(2)}`,
          backend_id: folder?.id ?? null,
          internal_path: folder?.internal_path || folder?.path || "",
          parent_id: folder?.parent_id ?? null,
          virtual: Boolean(folder?.virtual),
          entity_type: folder?.entity_type || null,
          entity_id: folder?.entity_id ?? null,
        }));
      } catch (error) {
        if (error.response && error.response.status !== 404) {
          this.toast.error('Failed to fetch files');
          console.error('Failed to fetch files (legacy):', error);
        }
      }
    },
    async fetchComments() {
      try {
        this.comments = await this.discussion.fetchComments({
          entityType: 'workflow',
          entityId: this.$route.params.id,
          entraId: this.loggedInUser.entra_id,
        });
      } catch (error) {
        this.toast.error('Failed to fetch comments');
        console.error('Failed to fetch comments:', error);
      }
    },
    async fetchUsers() {
      try {
        this.users = await this.discussion.fetchUsers({
          entraId: this.loggedInUser.entra_id,
        });
      } catch (error) {
        this.toast.error('Failed to fetch users');
        console.error('Failed to fetch users:', error);
      }
    },
    async fetchWorkflowMaterials() {
      try {
        const workflowId = this.$route.params.id;
        const response = await axios.get(API_ENDPOINTS.materialWorkflow, {
          params: {
            workflow_id: workflowId,
          }
        });
        this.materials = response.data;
      } catch (error) {
        this.toast.error('Failed to fetch projectMaterials');
      }
    },
    async addFiles({ files, id, folderId, fileType }) {
      this.uploadSession = {
        visible: true,
        fileType: fileType || 'workflow',
        entityId: id,
        files: Array.from(files),
        folderId: folderId || null,
      };
    },
    async handleUploadComplete() {
      await this.fetchFiles();
      if (this.$refs.filesComp?.fetchFolders) {
        await this.$refs.filesComp.fetchFolders();
      }
      this.uploadSession.visible = false;
    },
    handleUploadClose() {
      this.uploadSession.visible = false;
      this.fetchFiles();
    },
    handleUploadError(error) {
      console.error('Upload session error:', error);
    },
    async addAction({ action, workflowId }) {
      try {
        await apiService.createResource(API_ENDPOINTS.workflow(workflowId) + '/actions', action, this.loggedInUser.entra_id);
        this.fetchActions();
        this.toast.success('Activity added successfully!');
      } catch (error) {
        this.toast.error('Failed to add activity');
        console.error('Failed to add activity:', error);
      }
    },
    handleActionsReorder(actionIds) {
      // Optimistic UI: update local actions order based on reordered IDs
      const actionMap = new Map(this.actions.map(a => [a.id, a]));
      this.actions = actionIds.map(id => actionMap.get(id)).filter(Boolean);
    },
    async removeFile(fileId) {
      try {
        await apiService.deleteFile(fileId, this.loggedInUser?.id, this.loggedInUser?.entra_id);
        this.files = this.files.filter(file => file.id !== fileId);
        this.toast.success('File removed successfully!');
      } catch (error) {
        this.toast.error('Failed to remove file');
        console.error('Failed to remove file:', error);
      }
    },
    async addComment(commentText, author_id, files) {
      try {
        await this.discussion.addComment({
          entityType: 'workflow',
          entityId: this.$route.params.id,
          text: commentText,
          authorId: author_id,
          files,
          entraId: this.loggedInUser.entra_id,
        });

        if (files && files.length > 0) {
          await this.fetchFiles();
        }
        await this.fetchComments();
        this.toast.success('Comment added successfully!');
      } catch (error) {
        this.toast.error('Failed to add comment');
        console.error('Failed to add comment:', error);
      }
    },
    async removeComment(commentId) {
      try {
        await this.discussion.removeComment({
          commentId,
          userId: this.loggedInUser.id,
          entraId: this.loggedInUser.entra_id,
        });
        await this.fetchComments();
        await this.fetchFiles();
        this.toast.success('Comment removed successfully!');
      } catch (error) {
        this.toast.error('Failed to remove comment');
        console.error('Failed to remove comment:', error);
      }
    },
    async deleteAction(actionId) {
      try {
        await apiService.deleteResource(API_ENDPOINTS.action(actionId), this.loggedInUser.id, this.loggedInUser.entra_id);
        this.actions = this.actions.filter(action => action.id !== actionId);
        this.toast.success('Action deleted successfully!');
      } catch (error) {
        this.toast.error('Failed to delete action');
        console.error('Failed to delete action:', error);
      }
    },
    async updateWorkflowDetails(updatedWorkflow) {
      const workflowId = this.$route.params.id;
      try {
        const response = await apiService.updateResource(API_ENDPOINTS.workflow(workflowId), updatedWorkflow, this.loggedInUser.id, this.loggedInUser.entra_id);
        this.workflow = response.data;
        this.fetchWorkflowData();
        this.toast.success('Workflow updated successfully!');
      } catch (error) {
        this.toast.error('Failed to update workflow');
        console.error('Failed to update workflow:', error);
      }
    },
    async handleUpdateComment(commentId, newText, Id) {
      try {
        await this.discussion.updateComment({
          commentId,
          entityType: 'workflow',
          entityId: Id ?? this.$route.params.id,
          text: newText,
          userId: this.loggedInUser.id,
          entraId: this.loggedInUser.entra_id,
        });
        await this.fetchComments();
        this.toast.success("Comment updated successfully!");
      } catch (error) {
        this.toast.error('Failed to update comment');
        console.error('Failed to update comment:', error);
      }
    },
  },
};
</script>
  
  <style scoped>
  .tabs {
    display: flex;
    justify-content: center;
    margin-top: 10px;
    gap: 15px;
  }
  </style>
  